import * as marietta from '../..';
import { ITEM_MODE_NO, UNIT_BATH_DEFAULT_HEIGHT, UNIT_BATH_ITEM_ID } from '../Constants';
import { ItemInfoCollection } from '../collection';
import { MonicaObject } from '../item';

// UB の位置・天井高さ・商品を変える処理を足すときは、操作の確定（saveToHistory か commitWithoutHistory）を必ず通すこと
// 通さないと基準が古いまま残り、商品が外れた・居る部屋の天井が変わったことに気づけない

// 階ごとの基準天井高さのキー（autoMain.GetDesignHigh の戻り値）
const BASIC_CEILING_HEIGHT_KEYS: { readonly [floor: number]: string } = { 1: 'A1', 2: 'B1', 3: 'C1' };

/**
 * 確定したときの UB の状態（次の確定で、位置・天井高さ・商品の有無が変わったかを比べる）
 */
interface UnitBathBaseline {
  readonly x: number;
  readonly y: number;
  readonly floor: number;
  readonly ceiling: number;
  readonly isWhite: boolean;
}

/**
 * ユニットバスの高さを天井高さに合わせる
 * 作ったとき（置く・貼り付け・複製・LiDAR）は明示して、それ以外は操作の確定（commit）でまとめて合わせる。商品付き・手で変えた UB は合わせない
 */
export default class UnitBathUtil {
  // 前の確定のときの UB の状態（guid → 状態）。変える前の天井高さはここから取る
  private static baselines: Map<string, UnitBathBaseline> = new Map<string, UnitBathBaseline>();

  /**
   * 合わせる対象の UB なら、高さを置いた部屋の天井高さにする（UB 以外・商品付きは何もしない）
   */
  public static fitToCeiling(item: MonicaObject): void {
    if (!UnitBathUtil.isCeilingFitTarget(item)) return;
    // 縦の高さは itemInfo.depth に入る（JSON の height に当たる）
    item.itemInfo.depth = UnitBathUtil.getCeilingHeight(item.itemInfo.x, item.itemInfo.y, item.itemInfo.floor);
  }

  /**
   * 貼り付け・複製した UB は、コピー元が天井に連動していれば、置いた所の天井高さにする
   */
  public static fitToCeilingOnCopy(item: MonicaObject, source: ItemInfoCollection): void {
    if (!UnitBathUtil.isCeilingFitTarget(item) || !source) {
      return;
    }
    const sourceCeiling: number = UnitBathUtil.baselines.get(source.guid)?.ceiling
      ?? UnitBathUtil.getCeilingHeight(source.x, source.y, source.floor);
    if (UnitBathUtil.isFollowingCeiling(source.depth, sourceCeiling)) {
      UnitBathUtil.fitToCeiling(item);
    }
  }

  /**
   * 操作の確定。前の確定から位置・天井高さが変わった連動中の UB と、商品が外れた UB を合わせ、基準を作り直す
   * 前の確定に無い UB（作ったばかり）は合わせない（作った所で合わせ済み）
   */
  public static commit(): void {
    UnitBathUtil.getUnitBaths().forEach((item: MonicaObject) => {
      const { itemInfo } = item;
      const baseline: UnitBathBaseline | undefined = UnitBathUtil.baselines.get(itemInfo.guid);
      if (!baseline || !itemInfo.isWhiteItem()) return;
      if (!baseline.isWhite) {
        UnitBathUtil.fitToCeiling(item);
        return;
      }
      const ceiling: number = UnitBathUtil.getCeilingHeight(itemInfo.x, itemInfo.y, itemInfo.floor);
      const isMoved: boolean = itemInfo.x !== baseline.x || itemInfo.y !== baseline.y || itemInfo.floor !== baseline.floor;
      if ((isMoved || ceiling !== baseline.ceiling) && UnitBathUtil.isFollowingCeiling(itemInfo.depth, baseline.ceiling)) {
        itemInfo.depth = ceiling;
      }
    });
    UnitBathUtil.resetBaseline();
  }

  /**
   * 合わせずに、今の状態を基準にする（開いたとき・元に戻す・やり直しで、復元した高さを変えないため）
   */
  public static resetBaseline(): void {
    UnitBathUtil.baselines = new Map<string, UnitBathBaseline>();
    UnitBathUtil.getUnitBaths().forEach((item: MonicaObject) => {
      const { itemInfo } = item;
      UnitBathUtil.baselines.set(itemInfo.guid, {
        x: itemInfo.x,
        y: itemInfo.y,
        floor: itemInfo.floor,
        ceiling: UnitBathUtil.getCeilingHeight(itemInfo.x, itemInfo.y, itemInfo.floor),
        isWhite: itemInfo.isWhiteItem(),
      });
    });
  }

  /**
   * 図面にある UB（全階）
   */
  private static getUnitBaths(): MonicaObject[] {
    // 起動中で図面がまだ無いときも saveToHistory から呼ばれる
    if (!marietta.webcad.WebCADGlobal.itemManager || !marietta.webcad.WebCADGlobal.layerGroup) return [];
    const items: MonicaObject[] = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_EQUIPMENT_NO);
    return items.filter((item: MonicaObject) => item.itemInfo.id === UNIT_BATH_ITEM_ID);
  }

  /**
   * 天井高さに合わせる対象か（UB で、商品が付いていない）
   */
  private static isCeilingFitTarget(item: MonicaObject): boolean {
    return item?.itemInfo.id === UNIT_BATH_ITEM_ID && item.itemInfo.isWhiteItem();
  }

  /**
   * 高さが天井に連動しているか。未設定（0）か、変える前の天井高さと同じなら連動、違えば手で変えた値とみなす
   */
  private static isFollowingCeiling(depth: number, ceiling: number): boolean {
    return !(depth > 0) || Math.round(depth) === Math.round(ceiling);
  }

  /**
   * 点を含む部屋の天井高さ（基準天井高さ − 下がり天井）。部屋が無ければ基準天井高さ、取れなければ初期値
   */
  private static getCeilingHeight(x: number, y: number, floor: number): number {
    const designHeight: { [key: string]: string } = marietta.webcad.WebCADGlobal.autoMain.GetDesignHigh();
    const basicHeight: number = Number(designHeight[BASIC_CEILING_HEIGHT_KEYS[floor]]);
    const room: marietta.webcad.item.Room | null = marietta.webcad.util.WebCADUtil.getRoomAtPoint(new marietta.core.Point(x, y), floor);
    const ceilingHeight: number = basicHeight - (room?.itemInfo.ceiling_hang_height ?? 0);
    return ceilingHeight > 0 ? ceilingHeight : UNIT_BATH_DEFAULT_HEIGHT;
  }
}
